import React, { useMemo, useState } from "react";
import {
  BarChart3, TrendingUp, TrendingDown, Target, Crosshair,
  DollarSign, Activity, Award, Zap, ChevronLeft, ChevronRight,
} from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  ArcElement, Title, Tooltip, Legend, Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Doughnut, Line, Scatter } from "react-chartjs-2";

ChartJS.register(
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  ArcElement, Title, Tooltip, Legend, Filler, ChartDataLabels,
);

ChartJS.defaults.color = "#64748b";
ChartJS.defaults.font.family = "system-ui, -apple-system, sans-serif";

const _GREEN = "#22c55e";
const _RED = "#ef4444";
const _BLUE = "#2563eb";
const _AMBER = "#f59e0b";
const _TEAL = "#0f766e";
const _VIOLET = "#7c3aed";
const _MUTED = "#64748b";
const _GRID = "rgba(148,163,184,0.25)";

const bg = (c, o = 0.7) => `rgba(${parseInt(c.slice(1,3),16)},${parseInt(c.slice(3,5),16)},${parseInt(c.slice(5,7),16)},${o})`;
const chartOpts = (extra = {}) => ({
  responsive: true,
  maintainAspectRatio: true,
  plugins: {
    legend: { labels: { color: "#94a3b8", boxWidth: 12, padding: 12 } },
    datalabels: { display: false },
    ...extra.plugins,
  },
  scales: {
    x: { ticks: { color: _MUTED }, grid: { color: _GRID } },
    y: { ticks: { color: _MUTED }, grid: { color: _GRID } },
    ...extra.scales,
  },
  ...extra.rem,
});

const noDataMsg = (msg) => (
  <div className="flex items-center justify-center h-48 text-ics-gray text-sm">{msg || "No data"}</div>
);

function fmtNumber(v, d) {
  if (v == null || isNaN(v)) return "-";
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: d || 0, maximumFractionDigits: d || 2 });
}

function fmtSigned(v, suffix) {
  if (v == null || isNaN(v)) return "-";
  const num = Number(v);
  return (num > 0 ? "+" : "") + num.toFixed(2) + (suffix || "");
}

function fmtPct(v) {
  if (v == null || isNaN(v)) return "-";
  return Number(v).toFixed(1) + "%";
}

function fmtDays(v) {
  if (v == null || isNaN(v)) return "-";
  return Number(v).toFixed(1) + "d";
}

function signColor(v, inv = false) {
  if (v == null || isNaN(v)) return "text-ics-gray";
  const n = Number(v);
  if (inv) return n <= 0 ? "text-green-400" : "text-red-400";
  return n >= 0 ? "text-green-400" : "text-red-400";
}

function ChartPanel({ title, subtitle, children, className = "" }) {
  return (
    <div className={`bg-white/5 rounded-xl p-4 border border-white/10 ${className}`}>
      <div className="fw-bold text-ics-white mb-1">{title}</div>
      {subtitle && <div className="text-xs text-ics-gray mb-3">{subtitle}</div>}
      {children}
    </div>
  );
}

function KpiCard({ label, value, hint, color }) {
  return (
    <div className="bg-ics-navy-light rounded-xl p-4 border border-white/5">
      <div className="label text-xs text-ics-gray font-semibold uppercase tracking-wider">{label}</div>
      <div className={`value text-xl font-extrabold ${color || "text-ics-white"}`}>{value}</div>
      {hint != null && <div className="hint text-xs text-ics-gray mt-0.5">{hint}</div>}
    </div>
  );
}

const gridStyle = "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3";

function PrimaryStats({ summary, overall }) {
  return (
    <div className={gridStyle}>
      <KpiCard label="Total Signals" value={fmtNumber(summary?.total)} hint={`${fmtNumber(summary?.analyzed)} analyzed / ${fmtNumber(summary?.failed)} failed`} />
      <KpiCard label="Take Profit" value={fmtNumber(summary?.tp_total)} hint={`TP1:${fmtNumber(summary?.tp1)} TP2:${fmtNumber(summary?.tp2)} TP3:${fmtNumber(summary?.tp3)} TP4:${fmtNumber(summary?.tp4)}`} color="text-green-400" />
      <KpiCard label="Avg ROI" value={fmtSigned(summary?.avg_roi, "%")} color={signColor(summary?.avg_roi)} />
      <KpiCard label="Profit Factor" value={overall?.profit_factor != null ? Number(overall.profit_factor).toFixed(2) : "-"} color={Number(overall?.profit_factor || 0) >= 1 ? "text-green-400" : "text-red-400"} />
    </div>
  );
}

function MiniStats({ summary, overall }) {
  return (
    <div className={gridStyle}>
      <KpiCard label="Filtered Table" value={fmtNumber(overall?.analyzed)} hint={`${fmtNumber(overall?.total)} total / ${fmtNumber(overall?.failed)} failed`} />
      <KpiCard label="TP/SL Ratio" value={overall?.tp_sl_ratio != null ? Number(overall.tp_sl_ratio).toFixed(2) : (overall?.tp_hits != null && overall?.sl_hits ? (overall.tp_hits / overall.sl_hits).toFixed(2) : "-")} color="text-green-400" />
      <KpiCard label="Net ROI Sum" value={fmtSigned(overall?.total_roi, "%")} color={signColor(overall?.total_roi)} hint={`Gross: ${fmtSigned(overall?.gross_gain_roi, "%")} / ${fmtSigned(overall?.gross_loss_roi, "%")}`} />
      <KpiCard label="Compounded Model" value={overall?.compounded_roi != null ? fmtSigned(overall.compounded_roi, "%") : fmtSigned(summary?.compounded_roi, "%")} color={signColor(overall?.compounded_roi ?? summary?.compounded_roi)} />
    </div>
  );
}

function DashboardKpis({ overall }) {
  const cards = [
    { label: "Win Quality", value: fmtPct(overall?.win_rate), color: "text-green-400", hint: `${fmtNumber(overall?.tp_hits)}W / ${fmtNumber(overall?.sl_hits)}L` },
    { label: "Net ROI Sum", value: fmtSigned(overall?.total_roi, "%"), color: signColor(overall?.total_roi), hint: `${fmtSigned(overall?.gross_gain_roi, "%")} / ${fmtSigned(overall?.gross_loss_roi, "%")}` },
    { label: "Average Trade", value: fmtSigned(overall?.avg_roi, "%"), color: signColor(overall?.avg_roi), hint: `Expectancy: ${overall?.expectancy != null ? Number(overall.expectancy).toFixed(2) : "-"}` },
    { label: "Risk Cushion", value: overall?.avg_win_roi != null && overall?.avg_loss_roi ? Number(overall.avg_win_roi / Math.abs(overall.avg_loss_roi)).toFixed(2) : "-", hint: "Avg Win / Avg Loss" },
    { label: "Participation", value: overall?.analyzed != null && overall?.total ? fmtPct((overall.analyzed / overall.total) * 100) : "-", hint: `${fmtNumber(overall?.analyzed)} / ${fmtNumber(overall?.total)}` },
    { label: "Payoff Shape", value: overall?.payoff_ratio != null ? Number(overall.payoff_ratio).toFixed(2) : "-", hint: overall?.payoff_ratio >= 1 ? "Favorable" : "Unfavorable", color: Number(overall?.payoff_ratio || 0) >= 1 ? "text-green-400" : "text-red-400" },
    { label: "Compounded Model", value: fmtSigned(overall?.compounded_roi, "%"), color: signColor(overall?.compounded_roi) },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
      {cards.map((c, i) => <KpiCard key={i} {...c} />)}
    </div>
  );
}

function PrimaryPeriodChart({ primaryPeriod }) {
  const data = useMemo(() => {
    if (!primaryPeriod?.rows || !primaryPeriod.rows.length) return null;
    const labels = primaryPeriod.rows.map(m => m.label);
    const rois = primaryPeriod.rows.map(m => m.avg_roi ?? m.total_roi ?? 0);
    const winRates = primaryPeriod.rows.map(m => m.win_rate ?? 0);
    return {
      labels,
      datasets: [
        {
          label: "ROI %", type: "bar", data: rois,
          backgroundColor: rois.map(v => v >= 0 ? bg(_GREEN, 0.7) : bg(_RED, 0.7)),
          borderRadius: 4, yAxisID: "y",
          datalabels: { display: true, color: "#94a3b8", anchor: "end", align: "end", font: { size: 9 }, formatter: v => v.toFixed(1) },
        },
        {
          label: "Win Rate %", type: "line", data: winRates,
          borderColor: _BLUE, backgroundColor: bg(_BLUE, 0.1), pointRadius: 3, pointBackgroundColor: _BLUE,
          tension: 0.3, yAxisID: "y1", fill: false,
          datalabels: { display: true, color: _BLUE, anchor: "end", align: "end", font: { size: 9 }, formatter: v => v.toFixed(1) },
        },
      ],
    };
  }, [primaryPeriod]);

  if (!data) return noDataMsg("No period data");
  const opts = chartOpts({
    scales: {
      y: { position: "left", grid: { color: _GRID }, ticks: { color: _MUTED }, title: { display: true, text: "ROI %", color: "#94a3b8" } },
      y1: { position: "right", grid: { display: false }, ticks: { color: _MUTED }, title: { display: true, text: "Win Rate %", color: "#94a3b8" } },
    },
  });
  return (
    <ChartPanel title={primaryPeriod?.title || "Performance"} subtitle={primaryPeriod?.subtitle || "ROI % (bars) and Win Rate % (line)"}>
      <Bar data={data} options={opts} />
    </ChartPanel>
  );
}

function OutcomeChart({ overall }) {
  const data = useMemo(() => {
    const labels = ["Take Profit", "Stop Loss", "Open", "Failed"];
    const values = [
      overall?.tp_hits ?? 0,
      overall?.sl_hits ?? 0,
      overall?.open ?? 0,
      overall?.failed ?? 0,
    ];
    if (values.every(v => v === 0)) return null;
    return {
      labels,
      datasets: [{
        data: values,
        backgroundColor: [bg(_GREEN, 0.8), bg(_RED, 0.8), bg(_AMBER, 0.8), bg(_VIOLET, 0.8)],
        borderColor: [_GREEN, _RED, _AMBER, _VIOLET],
        borderWidth: 1,
        datalabels: {
          display: true, color: "#f8fafc", font: { weight: "bold", size: 11 },
          formatter: (v) => v || "", anchor: "center",
        },
      }],
    };
  }, [overall]);

  if (!data) return noDataMsg("No outcome data");
  return (
    <ChartPanel title="Outcome Mix" subtitle="Distribution of trade outcomes">
      <div className="flex justify-center">
        <div className="w-64 h-64">
          <Doughnut data={data} options={{
            responsive: true, maintainAspectRatio: true,
            plugins: {
              legend: { position: "bottom", labels: { color: "#94a3b8", boxWidth: 12, padding: 12 } },
              datalabels: { display: true, color: "#f8fafc", font: { weight: "bold", size: 11 }, formatter: (v) => v || "", anchor: "center" },
            },
          }} />
        </div>
      </div>
    </ChartPanel>
  );
}

function EquityChart({ equity }) {
  const data = useMemo(() => {
    if (!equity || !equity.length) return null;
    return {
      labels: equity.map(e => e.date),
      datasets: [{
        label: "Equity", data: equity.map(e => e.equity ?? 0),
        borderColor: _BLUE, backgroundColor: bg(_BLUE, 0.15), fill: true, tension: 0.3, pointRadius: 0,
      }],
    };
  }, [equity]);

  if (!data) return noDataMsg("No equity data");
  return (
    <ChartPanel title="Equity Curve" subtitle="Account equity over time">
      <Line data={data} options={chartOpts({ plugins: { legend: { display: false } } })} />
    </ChartPanel>
  );
}

function RunLeaders({ overall, best_ticker, best_month, best_weekday, best_rsi }) {
  const items = [
    { icon: Award, label: "Best Ticker", value: best_ticker?.ticker || "-", sub: "" },
    { icon: Award, label: "Best Month", value: best_month?.label || "-", sub: "" },
    { icon: Award, label: "Best Weekday", value: best_weekday?.label || "-", sub: "" },
    { icon: Award, label: "Best RSI Zone", value: best_rsi?.label || "-", sub: "" },
    { icon: TrendingUp, label: "Best Trade", value: overall?.best_trade?.roi != null ? fmtSigned(overall.best_trade.roi, "%") : "-", sub: overall?.best_trade?.ticker || "" },
    { icon: TrendingDown, label: "Worst Trade", value: overall?.worst_trade?.roi != null ? fmtSigned(overall.worst_trade.roi, "%") : "-", sub: overall?.worst_trade?.ticker || "" },
  ];

  return (
    <ChartPanel title="Run Leaders" subtitle="Top performers in each category">
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
            <div className="flex items-center gap-2">
              <item.icon className="w-3.5 h-3.5 text-ics-orange" />
              <span className="text-xs text-ics-gray">{item.label}</span>
            </div>
            <div className="text-right">
              <span className={`text-sm font-semibold ${item.value?.startsWith?.("+") || item.value === "-" ? "" : signColor(parseFloat(item.value))} text-ics-white`}>{item.value}</span>
              {item.sub && <div className="text-[10px] text-ics-gray">{item.sub}</div>}
            </div>
          </div>
        ))}
      </div>
    </ChartPanel>
  );
}

function DailyChart({ daily }) {
  const data = useMemo(() => {
    if (!daily || !daily.length) return null;
    const labels = daily.map(d => d.date);
    const profits = daily.map(d => d.daily_profit ?? 0);
    const cum = daily.map(d => d.cumulative ?? d.cum ?? 0);
    return {
      labels,
      datasets: [
        {
          label: "Daily ROI %", type: "bar", data: profits,
          backgroundColor: profits.map(v => v >= 0 ? bg(_GREEN, 0.7) : bg(_RED, 0.7)),
          borderRadius: 2, yAxisID: "y",
          datalabels: { display: false },
        },
        {
          label: "Cumulative %", type: "line", data: cum,
          borderColor: _BLUE, backgroundColor: bg(_BLUE, 0.1), pointRadius: 0,
          tension: 0.3, yAxisID: "y1", fill: false,
          datalabels: { display: false },
        },
      ],
    };
  }, [daily]);

  if (!data) return noDataMsg("No daily data");
  const opts = chartOpts({
    scales: {
      y: { position: "left", ticks: { color: _MUTED }, grid: { color: _GRID }, title: { display: true, text: "Daily ROI %", color: "#94a3b8" } },
      y1: { position: "right", grid: { display: false }, ticks: { color: _MUTED }, title: { display: true, text: "Cumulative %", color: "#94a3b8" } },
    },
  });
  return (
    <ChartPanel title="Daily ROI Growth" subtitle="Daily profit/loss with cumulative curve">
      <Bar data={data} options={opts} />
    </ChartPanel>
  );
}

function DrawdownChart({ equity }) {
  const data = useMemo(() => {
    if (!equity || !equity.length) return null;
    return {
      labels: equity.map(e => e.date),
      datasets: [{
        label: "Drawdown %",
        data: equity.map(e => {
          const dd = e.drawdown ?? 0;
          return dd >= 0 ? -dd : dd;
        }),
        borderColor: _RED, backgroundColor: bg(_RED, 0.15), fill: true, tension: 0.3, pointRadius: 0,
      }],
    };
  }, [equity]);

  if (!data) return noDataMsg("No drawdown data");
  return (
    <ChartPanel title="Drawdown Map" subtitle="Drawdown over time">
      <Line data={data} options={chartOpts({
        plugins: { legend: { display: false } },
        scales: { y: { ticks: { color: _MUTED }, grid: { color: _GRID } } },
      })} />
    </ChartPanel>
  );
}

function RsiChart({ rsi }) {
  const data = useMemo(() => {
    if (!rsi || !rsi.length) return null;
    const labels = rsi.map(r => r.label);
    const totals = rsi.map(r => r.total ?? 0);
    const winRates = rsi.map(r => r.win_rate ?? 0);
    const rois = rsi.map(r => r.total_roi ?? 0);
    return {
      labels,
      datasets: [
        { label: "Signals", type: "bar", data: totals, backgroundColor: bg(_BLUE, 0.7), borderRadius: 4, yAxisID: "y", datalabels: { display: false } },
        { label: "Win Rate %", type: "line", data: winRates, borderColor: _GREEN, backgroundColor: bg(_GREEN, 0.1), pointRadius: 3, pointBackgroundColor: _GREEN, tension: 0.3, yAxisID: "y1", fill: false, datalabels: { display: false } },
        { label: "Total ROI %", type: "line", data: rois, borderColor: _AMBER, backgroundColor: bg(_AMBER, 0.1), pointRadius: 3, pointBackgroundColor: _AMBER, tension: 0.3, yAxisID: "y2", fill: false, datalabels: { display: false } },
      ],
    };
  }, [rsi]);

  if (!data) return noDataMsg("No RSI data");
  const opts = chartOpts({
    scales: {
      y: { position: "left", ticks: { color: _MUTED }, grid: { color: _GRID }, title: { display: true, text: "Signals", color: "#94a3b8" } },
      y1: { position: "right", grid: { display: false }, ticks: { color: _MUTED }, title: { display: true, text: "Win Rate %", color: "#94a3b8" } },
      y2: { position: "right", grid: { display: false }, ticks: { color: _MUTED }, title: { display: true, text: "ROI %", color: "#94a3b8" }, offset: true },
    },
  });
  return (
    <ChartPanel title="RSI Map" subtitle="Performance across RSI zones">
      <Bar data={data} options={opts} />
    </ChartPanel>
  );
}

function WeekdayChart({ weekday }) {
  const data = useMemo(() => {
    if (!weekday || !weekday.length) return null;
    const labels = weekday.map(w => w.label);
    const rois = weekday.map(w => w.total_roi ?? 0);
    const winRates = weekday.map(w => w.win_rate ?? 0);
    return {
      labels,
      datasets: [
        {
          label: "ROI %", type: "bar", data: rois,
          backgroundColor: rois.map(v => v >= 0 ? bg(_GREEN, 0.7) : bg(_RED, 0.7)),
          borderRadius: 4, yAxisID: "y",
          datalabels: { display: true, color: "#94a3b8", anchor: "end", align: "end", font: { size: 9 }, formatter: v => v.toFixed(1) },
        },
        {
          label: "Win Rate %", type: "line", data: winRates,
          borderColor: _BLUE, backgroundColor: bg(_BLUE, 0.1), pointRadius: 3, pointBackgroundColor: _BLUE,
          tension: 0.3, yAxisID: "y1", fill: false,
          datalabels: { display: true, color: _BLUE, anchor: "end", align: "end", font: { size: 9 }, formatter: v => v.toFixed(1) },
        },
      ],
    };
  }, [weekday]);

  if (!data) return noDataMsg("No weekday data");
  const opts = chartOpts({
    scales: {
      y: { position: "left", ticks: { color: _MUTED }, grid: { color: _GRID }, title: { display: true, text: "ROI %", color: "#94a3b8" } },
      y1: { position: "right", grid: { display: false }, ticks: { color: _MUTED }, title: { display: true, text: "Win Rate %", color: "#94a3b8" } },
    },
  });
  return (
    <ChartPanel title="Weekday Performance" subtitle="ROI and win rate by day of week">
      <Bar data={data} options={opts} />
    </ChartPanel>
  );
}

function TickerLeaderboard({ topTickers, bottomTickers }) {
  const combined = useMemo(() => {
    const top = topTickers && topTickers.length ? topTickers : [];
    const bottom = bottomTickers && bottomTickers.length ? bottomTickers : [];
    const all = [...top, ...bottom];
    if (!all.length) return null;
    const sorted = all.sort((a, b) => (b.total_roi ?? 0) - (a.total_roi ?? 0)).slice(0, 15);
    return {
      labels: sorted.map(t => t.ticker),
      datasets: [{
        label: "Total ROI %", data: sorted.map(t => t.total_roi ?? 0),
        backgroundColor: sorted.map(t => (t.total_roi ?? 0) >= 0 ? bg(_GREEN, 0.7) : bg(_RED, 0.7)),
        borderRadius: 4,
        datalabels: {
          display: true, color: "#94a3b8", anchor: "end", align: "end", font: { size: 9 },
          formatter: v => v.toFixed(1),
        },
      }],
    };
  }, [topTickers, bottomTickers]);

  if (!combined) return noDataMsg("No ticker data");
  return (
    <ChartPanel title="Ticker Leaderboard" subtitle="ROI by ticker">
      <Bar data={combined} options={chartOpts({
        indexAxis: "y", plugins: { legend: { display: false } },
        scales: { x: { ticks: { color: _MUTED }, grid: { color: _GRID } }, y: { ticks: { color: "#94a3b8", font: { size: 10 } }, grid: { display: false } } },
      })} />
    </ChartPanel>
  );
}

function RoiDistribution({ roiDist }) {
  const data = useMemo(() => {
    if (!roiDist || !roiDist.length) return null;
    const labels = roiDist.map(r => r.label);
    const counts = roiDist.map(r => r.count ?? 0);
    return {
      labels,
      datasets: [{
        label: "Count", data: counts,
        backgroundColor: labels.map(l => {
          if (l === "0" || l === "0%") return bg(_MUTED, 0.5);
          const num = parseFloat(l);
          return num >= 0 ? bg(_GREEN, 0.7) : bg(_RED, 0.7);
        }),
        borderRadius: 4,
        datalabels: { display: true, color: "#94a3b8", anchor: "end", align: "end", font: { size: 9 } },
      }],
    };
  }, [roiDist]);

  if (!data) return noDataMsg("No ROI distribution");
  return (
    <ChartPanel title="ROI Distribution" subtitle="Distribution of trade ROI buckets">
      <Bar data={data} options={chartOpts({ plugins: { legend: { display: false } } })} />
    </ChartPanel>
  );
}

function TargetEfficiency({ targetEff }) {
  const data = useMemo(() => {
    if (!targetEff || !targetEff.length) return null;
    const labels = targetEff.map(t => t.target);
    const rates = targetEff.map(t => t.hit_rate ?? 0);
    const colors = [_GREEN, _TEAL, _BLUE, _MUTED];
    return {
      labels,
      datasets: [{
        label: "Hit Rate %", data: rates,
        backgroundColor: rates.map((_, i) => bg(colors[i % colors.length], 0.7)),
        borderRadius: 4,
        datalabels: {
          display: true, color: "#f8fafc", font: { weight: "bold", size: 10 },
          anchor: "center", formatter: (v) => v.toFixed(1) + "%",
        },
      }],
    };
  }, [targetEff]);

  if (!data) return noDataMsg("No target efficiency data");
  return (
    <ChartPanel title="Target Efficiency Funnel" subtitle="Hit rate by take profit target">
      <Bar data={data} options={chartOpts({ plugins: { legend: { display: false } } })} />
    </ChartPanel>
  );
}

function FirstHitMap({ firstHits }) {
  const data = useMemo(() => {
    if (!firstHits || !firstHits.length) return null;
    const isTp = (s) => s && (s.toLowerCase().includes("tp") || s.toLowerCase().includes("take") || s === "win");
    return {
      labels: firstHits.map(f => f.label),
      datasets: [{
        label: "Total", data: firstHits.map(f => f.total ?? 0),
        backgroundColor: firstHits.map(f => f.key != null && isTp(f.key) ? bg(_GREEN, 0.7) : bg(_RED, 0.7)),
        borderRadius: 4,
        datalabels: { display: true, color: "#94a3b8", anchor: "end", align: "end", font: { size: 9 } },
      }],
    };
  }, [firstHits]);

  if (!data) return noDataMsg("No first hit data");
  return (
    <ChartPanel title="First Hit Map" subtitle="Distribution of first target hits">
      <Bar data={data} options={chartOpts({ plugins: { legend: { display: false } } })} />
    </ChartPanel>
  );
}

function RsiFlags({ rsiFlags }) {
  const data = useMemo(() => {
    if (!rsiFlags || !rsiFlags.length) return null;
    const labels = rsiFlags.map(r => r.label);
    const rates = rsiFlags.map(r => r.win_rate ?? 0);
    return {
      labels,
      datasets: [{
        label: "Win Rate %", data: rates,
        backgroundColor: rates.map(v => v >= 50 ? bg(_GREEN, 0.7) : bg(_RED, 0.7)),
        borderRadius: 4,
        datalabels: {
          display: true, color: "#f8fafc", font: { weight: "bold", size: 9 },
          anchor: "center", formatter: (v) => v.toFixed(1) + "%",
        },
      }],
    };
  }, [rsiFlags]);

  if (!data) return noDataMsg("No RSI flag data");
  return (
    <ChartPanel title="RSI Signal Flags" subtitle="Win rate by RSI flag condition">
      <Bar data={data} options={chartOpts({
        indexAxis: "y", plugins: { legend: { display: false } },
        scales: { x: { ticks: { color: _MUTED }, grid: { color: _GRID } }, y: { ticks: { color: "#94a3b8", font: { size: 10 } }, grid: { display: false } } },
      })} />
    </ChartPanel>
  );
}

function AvgWinLoss({ riskRewardSummary }) {
  const data = useMemo(() => {
    if (!riskRewardSummary) return null;
    const aw = riskRewardSummary.avg_win ?? 0;
    const al = riskRewardSummary.avg_loss ?? 0;
    if (!aw && !al) return null;
    return {
      labels: ["Avg Win", "Avg Loss"],
      datasets: [{
        label: "ROI %", data: [aw, Math.abs(al)],
        backgroundColor: [bg(_GREEN, 0.7), bg(_RED, 0.7)],
        borderRadius: 4,
        datalabels: {
          display: true, color: "#f8fafc", font: { weight: "bold", size: 10 },
          anchor: "center", formatter: (v) => v.toFixed(2) + "%",
        },
      }],
    };
  }, [riskRewardSummary]);

  if (!data) return noDataMsg("No avg win/loss data");
  return (
    <ChartPanel title="Average Win vs Loss" subtitle="Comparison of avg winning vs losing trade">
      <Bar data={data} options={chartOpts({
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { color: _MUTED }, grid: { color: _GRID } } },
      })} />
    </ChartPanel>
  );
}

function DurationHistogram({ durationHist }) {
  const data = useMemo(() => {
    if (!durationHist || !durationHist.length) return null;
    return {
      labels: durationHist.map(d => d.label),
      datasets: [{
        label: "Count", data: durationHist.map(d => d.count ?? 0),
        backgroundColor: bg(_BLUE, 0.7), borderRadius: 4,
        datalabels: { display: true, color: "#94a3b8", anchor: "end", align: "end", font: { size: 9 } },
      }],
    };
  }, [durationHist]);

  if (!data) return noDataMsg("No duration data");
  return (
    <ChartPanel title="Duration Histogram" subtitle="Trade duration distribution">
      <Bar data={data} options={chartOpts({ plugins: { legend: { display: false } } })} />
    </ChartPanel>
  );
}

function Streaks({ streaks }) {
  return (
    <ChartPanel title="Winning / Losing Streaks" subtitle="Longest consecutive wins and losses">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20 text-center">
          <div className="text-xs text-ics-gray font-semibold uppercase tracking-wider mb-1">Winning Streak</div>
          <div className="text-2xl font-extrabold text-green-400">{streaks?.longest_win ?? "-"}</div>
        </div>
        <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/20 text-center">
          <div className="text-xs text-ics-gray font-semibold uppercase tracking-wider mb-1">Losing Streak</div>
          <div className="text-2xl font-extrabold text-red-400">{streaks?.longest_loss ?? "-"}</div>
        </div>
      </div>
    </ChartPanel>
  );
}

function RsiScatterChart({ rsiScatter }) {
  const data = useMemo(() => {
    if (!rsiScatter || !rsiScatter.length) return null;
    return {
      datasets: [
        {
          label: "Winners",
          data: rsiScatter.filter(d => d.y >= 0).map(d => ({ x: d.x, y: d.y, ticker: d.ticker })),
          backgroundColor: bg(_GREEN, 0.7), pointRadius: 4,
        },
        {
          label: "Losers",
          data: rsiScatter.filter(d => d.y < 0).map(d => ({ x: d.x, y: d.y, ticker: d.ticker })),
          backgroundColor: bg(_RED, 0.7), pointRadius: 4,
        },
      ],
    };
  }, [rsiScatter]);

  if (!data || !data.datasets[0].data.length && !data.datasets[1].data.length) return noDataMsg("No RSI scatter data");
  return (
    <ChartPanel title="RSI Sweet Spot Scatter" subtitle="ROI vs RSI value per trade">
      <Scatter data={data} options={chartOpts({
        plugins: {
          legend: { position: "top", labels: { color: "#94a3b8", boxWidth: 12, padding: 12 } },
          tooltip: { callbacks: { label: (ctx) => `${ctx.raw.ticker || ""}: ${ctx.raw.y.toFixed(2)}%` } },
        },
        scales: {
          x: { ticks: { color: _MUTED }, grid: { color: _GRID }, title: { display: true, text: "RSI", color: "#94a3b8" } },
          y: { ticks: { color: _MUTED }, grid: { color: _GRID }, title: { display: true, text: "ROI %", color: "#94a3b8" } },
        },
      })} />
    </ChartPanel>
  );
}

function RiskRewardChart({ riskReward }) {
  const data = useMemo(() => {
    if (!riskReward || !riskReward.length) return null;
    const labels = riskReward.map(r => r.label);
    const rois = riskReward.map(r => r.total_roi ?? 0);
    const winRates = riskReward.map(r => r.win_rate ?? 0);
    return {
      labels,
      datasets: [
        {
          label: "ROI %", type: "bar", data: rois,
          backgroundColor: rois.map(v => v >= 0 ? bg(_GREEN, 0.7) : bg(_RED, 0.7)),
          borderRadius: 4, yAxisID: "y",
          datalabels: { display: true, color: "#94a3b8", anchor: "end", align: "end", font: { size: 9 }, formatter: v => v.toFixed(1) },
        },
        {
          label: "Win Rate %", type: "line", data: winRates,
          borderColor: _BLUE, pointRadius: 3, pointBackgroundColor: _BLUE, tension: 0.3, yAxisID: "y1", fill: false,
          datalabels: { display: true, color: _BLUE, anchor: "end", align: "end", font: { size: 9 }, formatter: v => v.toFixed(1) },
        },
      ],
    };
  }, [riskReward]);

  if (!data) return noDataMsg("No risk/reward data");
  const opts = chartOpts({
    scales: {
      y: { position: "left", ticks: { color: _MUTED }, grid: { color: _GRID }, title: { display: true, text: "ROI %", color: "#94a3b8" } },
      y1: { position: "right", grid: { display: false }, ticks: { color: _MUTED }, title: { display: true, text: "Win Rate %", color: "#94a3b8" } },
    },
  });
  return (
    <ChartPanel title="Risk / Reward Zones" subtitle="ROI and win rate by risk-reward ratio">
      <Bar data={data} options={opts} />
    </ChartPanel>
  );
}

function ResolutionTimeChart({ resolutionTimes, resolutionSummary }) {
  const data = useMemo(() => {
    if (!resolutionTimes || !resolutionTimes.length) return null;
    const labels = resolutionTimes.map(r => r.ticker);
    const days = resolutionTimes.map(r => r.days ?? 0);
    const outcomes = resolutionTimes.map(r => r.outcome ?? "");
    const isWin = (s) => s && (s.toLowerCase().includes("tp") || s.toLowerCase().includes("take") || s === "win");
    return {
      labels,
      datasets: [{
        label: "Days", data: days,
        backgroundColor: outcomes.map(o => isWin(o) ? bg(_GREEN, 0.7) : bg(_RED, 0.7)),
        borderRadius: 4,
        datalabels: { display: true, color: "#94a3b8", anchor: "end", align: "end", font: { size: 8 }, formatter: v => v.toFixed(1) },
      }],
    };
  }, [resolutionTimes]);

  const stats = resolutionSummary ? { ...resolutionSummary } : {};
  const minV = stats.min?.days;
  const maxV = stats.max?.days;
  const avgV = stats.avg_days;

  return (
    <ChartPanel title="Resolution Time" subtitle="Days to resolve by ticker">
      {data ? <Bar data={data} options={chartOpts({ plugins: { legend: { display: false } } })} /> : noDataMsg("No resolution data")}
      <div className="grid grid-cols-3 gap-2 mt-3">
        <div className="bg-ics-navy-light rounded-lg p-2 text-center">
          <div className="text-[10px] text-ics-gray uppercase tracking-wider">Fastest</div>
          <div className="text-sm font-bold text-green-400">{minV != null ? fmtDays(minV) : "-"}</div>
          <div className="text-[10px] text-ics-gray truncate">{stats.min?.ticker || ""}</div>
        </div>
        <div className="bg-ics-navy-light rounded-lg p-2 text-center">
          <div className="text-[10px] text-ics-gray uppercase tracking-wider">Slowest</div>
          <div className="text-sm font-bold text-red-400">{maxV != null ? fmtDays(maxV) : "-"}</div>
          <div className="text-[10px] text-ics-gray truncate">{stats.max?.ticker || ""}</div>
        </div>
        <div className="bg-ics-navy-light rounded-lg p-2 text-center">
          <div className="text-[10px] text-ics-gray uppercase tracking-wider">Average</div>
          <div className="text-sm font-bold text-ics-white">{avgV != null ? fmtDays(avgV) : "-"}</div>
        </div>
      </div>
    </ChartPanel>
  );
}

function MonthlyQualityTable({ monthlyDepth }) {
  if (!monthlyDepth || !monthlyDepth.length) return null;
  return (
    <ChartPanel title="Monthly Quality" subtitle="Deep performance metrics by month">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-white/10 text-ics-gray uppercase tracking-wider">
              <th className="text-left px-2 py-2 font-semibold">Month</th>
              <th className="text-right px-2 py-2 font-semibold">Trades</th>
              <th className="text-right px-2 py-2 font-semibold">Win Rate</th>
              <th className="text-right px-2 py-2 font-semibold">ROI %</th>
              <th className="text-right px-2 py-2 font-semibold">Profit Factor</th>
              <th className="text-right px-2 py-2 font-semibold">Expectancy</th>
              <th className="text-right px-2 py-2 font-semibold">Efficiency</th>
              <th className="text-right px-2 py-2 font-semibold">Win Streak</th>
              <th className="text-right px-2 py-2 font-semibold">Loss Streak</th>
            </tr>
          </thead>
          <tbody>
            {monthlyDepth.map((m, i) => (
              <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-2 py-2 text-ics-white font-medium">{m.label}</td>
                <td className="px-2 py-2 text-right text-ics-gray">{fmtNumber(m.total)}</td>
                <td className={`px-2 py-2 text-right ${signColor((m.win_rate ?? 0) - 50)}`}>{fmtPct(m.win_rate)}</td>
                <td className={`px-2 py-2 text-right font-semibold ${signColor(m.total_roi)}`}>{fmtSigned(m.total_roi, "%")}</td>
                <td className="px-2 py-2 text-right text-ics-gray">{m.profit_factor != null ? Number(m.profit_factor).toFixed(2) : "-"}</td>
                <td className="px-2 py-2 text-right text-ics-gray">{m.expectancy != null ? Number(m.expectancy).toFixed(2) : "-"}</td>
                <td className="px-2 py-2 text-right text-ics-gray">{m.trade_efficiency != null ? Number(m.trade_efficiency).toFixed(1) + "%" : "-"}</td>
                <td className="px-2 py-2 text-right text-green-400">{m.longest_win_streak ?? "-"}</td>
                <td className="px-2 py-2 text-right text-red-400">{m.longest_loss_streak ?? "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartPanel>
  );
}

function TickerHeatMap({ tickerMonthly, heatmapMonths }) {
  const data = useMemo(() => {
    if (!tickerMonthly || !tickerMonthly.length || !heatmapMonths || !heatmapMonths.length) return null;
    const months = heatmapMonths;
    const tickers = tickerMonthly.map(t => t.ticker).filter(Boolean);
    if (!tickers.length) return null;

    const values = tickers.map(tkr => {
      const row = tickerMonthly.find(t => t.ticker === tkr);
      if (!row) return months.map(() => null);
      return months.map(m => {
        if (!row.months || row.months[m] == null) return null;
        if (typeof row.months[m] === "object") return row.months[m].roi ?? row.months[m].month_pct ?? null;
        return row.months[m];
      });
    });

    const allVals = values.flat().filter(v => v != null);
    if (!allVals.length) return null;
    const maxAbs = Math.max(...allVals.map(Math.abs), 0.01);

    return { tickers, months, values, maxAbs };
  }, [tickerMonthly, heatmapMonths]);

  if (!data) return null;

  const { tickers, months, values, maxAbs } = data;
  const cellSize = 36;
  const cellGap = 2;

  return (
    <ChartPanel title="Ticker / Month Heat Map" subtitle="ROI intensity by ticker and month">
      <div className="overflow-x-auto">
        <table className="text-xs border-collapse">
          <thead>
            <tr>
              <th className="text-ics-gray px-1 py-1 text-left font-semibold">Ticker</th>
              {months.map(m => (
                <th key={m} className="text-ics-gray px-1 py-1 text-center font-semibold" style={{ minWidth: cellSize }}>{m.split("-")[1]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tickers.map((tkr, ti) => (
              <tr key={tkr}>
                <td className="text-ics-white px-1 py-1 text-xs font-medium truncate max-w-[70px]">{tkr}</td>
                {months.map((m, mi) => {
                  const v = values[ti][mi];
                  let bgColor = "bg-ics-navy-light";
                  if (v != null) {
                    const intensity = Math.min(Math.abs(v) / maxAbs, 1);
                    const alpha = 0.15 + intensity * 0.7;
                    if (v >= 0) bgColor = `rgba(34,197,94,${alpha})`;
                    else bgColor = `rgba(239,68,68,${alpha})`;
                  }
                  return (
                    <td key={m} className="text-center px-1 py-1" style={{ minWidth: cellSize }}>
                      <div
                        className="rounded font-semibold flex items-center justify-center"
                        style={{ width: cellSize - cellGap, height: cellSize - cellGap, background: bgColor, color: v != null ? "#f8fafc" : "#64748b" }}
                        title={v != null ? `${tkr} ${m}: ${Number(v).toFixed(2)}%` : ""}
                      >
                        {v != null ? Number(v).toFixed(1) : "-"}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartPanel>
  );
}

function TradesTable({ trades }) {
  const [page, setPage] = useState(0);
  const perPage = 25;

  if (!trades || !Array.isArray(trades) || !trades.length) return null;

  const totalPages = Math.ceil(trades.length / perPage);
  const currentPage = Math.min(page, totalPages - 1);
  const sliced = trades.slice(currentPage * perPage, (currentPage + 1) * perPage);

  const outcomeBadge = (outcome, status) => {
    const o = outcome || status || "";
    const isWin = o.toLowerCase().includes("tp") || o === "win" || o === "tp1_hit" || o === "tp2_hit" || o === "tp3_hit" || o === "tp4_hit";
    const isLoss = o.toLowerCase().includes("sl") || o === "loss" || o === "sl_hit";
    const cls = isWin ? "bg-green-500/20 text-green-400" : isLoss ? "bg-red-500/20 text-red-400" : "bg-yellow-500/20 text-yellow-400";
    return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{o}</span>;
  };

  return (
    <div className="bg-ics-navy-light rounded-xl border border-white/5 overflow-hidden">
      <div className="p-4 border-b border-white/5 flex items-center justify-between">
        <h3 className="text-lg font-bold text-ics-white flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-ics-orange" />
          Trades
        </h3>
        <span className="text-xs text-ics-gray">{fmtNumber(trades.length)} total</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5 text-ics-gray text-xs uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-semibold">ID</th>
              <th className="text-left px-4 py-3 font-semibold">Ticker</th>
              <th className="text-left px-4 py-3 font-semibold">Outcome</th>
              <th className="text-left px-4 py-3 font-semibold">Market</th>
              <th className="text-right px-4 py-3 font-semibold">Entry</th>
              <th className="text-right px-4 py-3 font-semibold">Exit</th>
              <th className="text-right px-4 py-3 font-semibold">ROI %</th>
              <th className="text-right px-4 py-3 font-semibold">Duration</th>
              <th className="text-left px-4 py-3 font-semibold">Entry Date</th>
            </tr>
          </thead>
          <tbody>
            {sliced.map((t) => {
              const roi = Number(t.roi_percent || 0);
              const dur = t.duration_ms ? (t.duration_ms / 3600000).toFixed(1) + "h" : "-";
              const entryDate = t.entry_at ? new Date(t.entry_at).toLocaleDateString() : "-";
              return (
                <tr key={t.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2.5 text-ics-gray text-xs">{t.id}</td>
                  <td className="px-4 py-2.5 text-ics-white font-medium">{t.ticker || "-"}</td>
                  <td className="px-4 py-2.5">{outcomeBadge(t.outcome, t.status)}</td>
                  <td className="px-4 py-2.5 text-ics-gray text-xs">{t.market || "-"}</td>
                  <td className="px-4 py-2.5 text-right text-ics-gray text-xs">{t.entry_price != null ? Number(t.entry_price).toFixed(4) : "-"}</td>
                  <td className="px-4 py-2.5 text-right text-ics-gray text-xs">{t.exit_price != null ? Number(t.exit_price).toFixed(4) : "-"}</td>
                  <td className={`px-4 py-2.5 text-right font-semibold text-xs ${roi >= 0 ? "text-green-400" : "text-red-400"}`}>{roi >= 0 ? "+" : ""}{roi.toFixed(2)}%</td>
                  <td className="px-4 py-2.5 text-right text-ics-gray text-xs">{dur}</td>
                  <td className="px-4 py-2.5 text-ics-gray text-xs">{entryDate}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
          <span className="text-xs text-ics-gray">
            Page {currentPage + 1} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="p-1 rounded hover:bg-white/10 text-ics-gray disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
              className="p-1 rounded hover:bg-white/10 text-ics-gray disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MarketsChart({ markets }) {
  const data = useMemo(() => {
    if (!markets || !markets.length) return null;
    const labels = markets.map(m => m.label);
    const rois = markets.map(m => m.total_roi ?? 0);
    const winRates = markets.map(m => m.win_rate ?? 0);
    return {
      labels,
      datasets: [
        {
          label: "ROI %", type: "bar", data: rois,
          backgroundColor: rois.map(v => v >= 0 ? bg(_GREEN, 0.7) : bg(_RED, 0.7)),
          borderRadius: 4, yAxisID: "y",
          datalabels: { display: true, color: "#94a3b8", anchor: "end", align: "end", font: { size: 9 }, formatter: v => v.toFixed(1) },
        },
        {
          label: "Win Rate %", type: "line", data: winRates,
          borderColor: _BLUE, pointRadius: 3, pointBackgroundColor: _BLUE, tension: 0.3, yAxisID: "y1", fill: false,
          datalabels: { display: true, color: _BLUE, anchor: "end", align: "end", font: { size: 9 }, formatter: v => v.toFixed(1) },
        },
      ],
    };
  }, [markets]);

  if (!data) return null;
  const opts = chartOpts({
    scales: {
      y: { position: "left", ticks: { color: _MUTED }, grid: { color: _GRID }, title: { display: true, text: "ROI %", color: "#94a3b8" } },
      y1: { position: "right", grid: { display: false }, ticks: { color: _MUTED }, title: { display: true, text: "Win Rate %", color: "#94a3b8" } },
    },
  });
  return (
    <ChartPanel title="Markets Performance" subtitle="ROI and win rate by market">
      <Bar data={data} options={opts} />
    </ChartPanel>
  );
}

function BenchmarkChart({ benchmarkComparison }) {
  const chartData = useMemo(() => {
    if (!benchmarkComparison?.points?.length) return null;
    let pts = benchmarkComparison.points;
    if (pts.length > 200) { const s = Math.ceil(pts.length / 200); pts = pts.filter((_, i) => i % s === 0 || i === pts.length - 1); }
    
    // dynamically build datasets based on indices available
    const ds = [
      { label: "Strategy", data: pts.map(p => p.strategy), borderColor: "#3ddc84", backgroundColor: "rgba(61,220,132,0.13)", fill: false, pointRadius: 2, tension: 0.25, borderWidth: 2.5, spanGaps: true, yAxisID: 'y' }
    ];
    if (benchmarkComparison.indices?.includes('ndx')) {
      ds.push({ label: "NASDAQ-100 (NDX)", data: pts.map(p => p.ndx), borderColor: "#5b8cff", backgroundColor: "#5b8cff", fill: false, pointRadius: 1.5, tension: 0.25, borderWidth: 1.75, spanGaps: true, yAxisID: 'y1' });
      ds.push({ label: "S&P 500 (SPX)", data: pts.map(p => p.spx), borderColor: "#ffb454", backgroundColor: "#ffb454", fill: false, pointRadius: 1.5, tension: 0.25, borderWidth: 1.75, spanGaps: true, yAxisID: 'y1' });
      ds.push({ label: "Dow Jones (DJI)", data: pts.map(p => p.dji), borderColor: "#ff6b6b", backgroundColor: "#ff6b6b", fill: false, pointRadius: 1.5, tension: 0.25, borderWidth: 1.75, spanGaps: true, yAxisID: 'y1' });
    } else if (benchmarkComparison.indices?.includes('tasi')) {
      ds.push({ label: "TASI Index", data: pts.map(p => p.tasi), borderColor: "#5b8cff", backgroundColor: "#5b8cff", fill: false, pointRadius: 1.5, tension: 0.25, borderWidth: 1.75, spanGaps: true, yAxisID: 'y1' });
    }
    
    return { labels: pts.map(p => p.date), datasets: ds };
  }, [benchmarkComparison]);

  if (!chartData) return null;

  const hasNdx = benchmarkComparison.indices?.includes('ndx');
  const hasTasi = benchmarkComparison.indices?.includes('tasi');
  const titleStr = hasNdx ? "Strategy vs NDX / SPX / DJI" : (hasTasi ? "Strategy vs TASI Index" : "Strategy vs Index");
  
  return (
    <div className="bg-[#0f1420] rounded-2xl border border-[#2a3350] p-5 mb-4">
      <div className="text-lg font-bold text-ics-white">{titleStr}</div>
      <div className="text-xs text-ics-gray mb-4">Time-series comparison, normalized to 100 at the start of the trade log &nbsp;•&nbsp; {benchmarkComparison.first_month || benchmarkComparison.first_date || 'inception'} onwards</div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-[#171d2b] border border-[#2a3350] rounded-xl p-4">
          <div className="text-xs text-ics-gray flex items-center gap-1.5"><span className="w-2 h-2 rounded-full inline-block bg-[#3ddc84]" />Strategy</div>
          <div className={`text-[22px] font-bold mt-1.5 ${signColor(benchmarkComparison.summary?.strategy_return)}`}>{fmtSigned(benchmarkComparison.summary?.strategy_return, "%")}</div>
        </div>
        {hasNdx && (
          <>
            <div className="bg-[#171d2b] border border-[#2a3350] rounded-xl p-4">
              <div className="text-xs text-ics-gray flex items-center gap-1.5"><span className="w-2 h-2 rounded-full inline-block bg-[#5b8cff]" />NASDAQ-100 (NDX)</div>
              <div className={`text-[22px] font-bold mt-1.5 ${signColor(benchmarkComparison.summary?.ndx_return)}`}>{fmtSigned(benchmarkComparison.summary?.ndx_return, "%")}</div>
            </div>
            <div className="bg-[#171d2b] border border-[#2a3350] rounded-xl p-4">
              <div className="text-xs text-ics-gray flex items-center gap-1.5"><span className="w-2 h-2 rounded-full inline-block bg-[#ffb454]" />S&P 500 (SPX)</div>
              <div className={`text-[22px] font-bold mt-1.5 ${signColor(benchmarkComparison.summary?.spx_return)}`}>{fmtSigned(benchmarkComparison.summary?.spx_return, "%")}</div>
            </div>
            <div className="bg-[#171d2b] border border-[#2a3350] rounded-xl p-4">
              <div className="text-xs text-ics-gray flex items-center gap-1.5"><span className="w-2 h-2 rounded-full inline-block bg-[#ff6b6b]" />Dow Jones (DJI)</div>
              <div className={`text-[22px] font-bold mt-1.5 ${signColor(benchmarkComparison.summary?.dji_return)}`}>{fmtSigned(benchmarkComparison.summary?.dji_return, "%")}</div>
            </div>
          </>
        )}
        {hasTasi && (
          <div className="bg-[#171d2b] border border-[#2a3350] rounded-xl p-4">
            <div className="text-xs text-ics-gray flex items-center gap-1.5"><span className="w-2 h-2 rounded-full inline-block bg-[#5b8cff]" />TASI Index</div>
            <div className={`text-[22px] font-bold mt-1.5 ${signColor(benchmarkComparison.summary?.tasi_return)}`}>{fmtSigned(benchmarkComparison.summary?.tasi_return, "%")}</div>
          </div>
        )}
      </div>

      <div className="bg-[#171d2b] border border-[#2a3350] rounded-xl p-4">
        <div className="text-sm font-bold text-ics-white mb-1">Growth of $100, indexed at {benchmarkComparison.first_month || benchmarkComparison.first_date || 'inception'}</div>
        <div className="text-xs text-ics-gray mb-3">Strategy line built from trade log equity. Index lines are daily closes, indexed to the same start date.</div>
        <div style={{ height: 320 }}><Line data={chartData} options={{
          responsive: true, maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { labels: { color: "#e7ebf5", boxWidth: 14, boxHeight: 2, padding: 14, usePointStyle: true } },
            datalabels: { display: false },
            tooltip: {
              backgroundColor: "#1d2436", borderColor: "#2a3350", borderWidth: 1,
              callbacks: {
                label: (ctx) => {
                  const val = ctx.parsed.y;
                  const chg = val != null ? (val - 100).toFixed(2) : '-';
                  return ` ${ctx.dataset.label}: ${val != null ? val.toFixed(2) : '-'} (${chg >= 0 ? '+' : ''}${chg}%)`;
                },
              },
            },
          },
          scales: {
            x: { grid: { color: "#2a3350" }, ticks: { color: "#9aa4c2", font: { size: 10 }, maxTicksLimit: 12 } },
            y: { type: 'linear', display: true, position: 'left', grid: { color: "#2a3350" }, ticks: { color: "#9aa4c2", callback: (v) => v.toFixed(0) }, title: { display: true, text: 'Strategy', color: '#3ddc84', font: { size: 11 } } },
            y1: { type: 'linear', display: true, position: 'right', grid: { drawOnChartArea: false }, ticks: { color: "#9aa4c2", callback: (v) => v.toFixed(0) }, title: { display: true, text: 'Indices', color: '#5b8cff', font: { size: 11 } } },
          },
        }} /></div>
      </div>
    </div>
  );
}

export default function ReportDashboard({ data, loading }) {
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-4 animate-pulse">
        <div className="h-12 bg-ics-navy-light rounded-xl w-1/3" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-ics-navy-light rounded-xl" />)}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-ics-navy-light rounded-xl" />)}
        </div>
        <div className="h-80 bg-ics-navy-light rounded-xl" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16">
        <BarChart3 className="w-16 h-16 text-ics-gray/30 mx-auto mb-4" />
        <p className="text-ics-gray text-lg">No report data available for this analysis.</p>
      </div>
    );
  }

  const run = data.run || {};
  const summary = data.summary || {};
  const dashboard = data.dashboard || {};
  const overall = dashboard.overall || {};
  const trades = data.trades || [];

  return (
    <div className="max-w-7xl mx-auto space-y-8">

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-ics-white">{run.name}</h2>
          <p className="text-ics-gray mt-1">{run.account}</p>
          <p className="text-sm text-ics-gray">{run.dateRange}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${run.status === "completed" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>
            {run.status}
          </span>
          {run.type && <span className="text-xs text-ics-gray bg-ics-navy-light px-2 py-1 rounded">{run.type}</span>}
          {run.market && <span className="text-xs text-ics-gray bg-ics-navy-light px-2 py-1 rounded">{run.market}</span>}
        </div>
      </div>

      <PrimaryStats summary={summary} overall={overall} />

      <MiniStats summary={summary} overall={overall} />

      <div className="space-y-6">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-5 h-5 text-ics-orange" />
          <h3 className="text-xl font-bold text-ics-white">Dashboard</h3>
        </div>

        <DashboardKpis overall={overall} />

        {dashboard.benchmark_comparison && <BenchmarkChart benchmarkComparison={dashboard.benchmark_comparison} />}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {dashboard.primary_period && dashboard.primary_period.rows?.length > 0 ? <PrimaryPeriodChart primaryPeriod={dashboard.primary_period} /> : null}
          <div className="grid grid-cols-1 gap-4">
            <OutcomeChart outcomes={dashboard.outcomes} overall={overall} />
            <RunLeaders
              overall={overall}
              best_ticker={dashboard.best_ticker}
              best_month={dashboard.best_month}
              best_weekday={dashboard.best_weekday}
              best_rsi={dashboard.best_rsi}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {dashboard.equity && dashboard.equity.length > 0 ? <EquityChart equity={dashboard.equity} /> : null}
          {dashboard.daily && dashboard.daily.length > 0 ? <DailyChart daily={dashboard.daily} /> : null}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {dashboard.equity && dashboard.equity.length > 0 ? <DrawdownChart equity={dashboard.equity} /> : null}
          {dashboard.rsi && dashboard.rsi.length > 0 ? <RsiChart rsi={dashboard.rsi} /> : null}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {dashboard.weekday && dashboard.weekday.length > 0 ? <WeekdayChart weekday={dashboard.weekday} /> : null}
          {dashboard.top_tickers && dashboard.top_tickers.length > 0 || dashboard.bottom_tickers && dashboard.bottom_tickers.length > 0
            ? <TickerLeaderboard topTickers={dashboard.top_tickers} bottomTickers={dashboard.bottom_tickers} /> : null}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {dashboard.roi_distribution && dashboard.roi_distribution.length > 0 ? <RoiDistribution roiDist={dashboard.roi_distribution} /> : null}
          {dashboard.target_efficiency && dashboard.target_efficiency.length > 0 ? <TargetEfficiency targetEff={dashboard.target_efficiency} /> : null}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {dashboard.first_hits && dashboard.first_hits.length > 0 ? <FirstHitMap firstHits={dashboard.first_hits} /> : null}
          {dashboard.rsi_flags && dashboard.rsi_flags.length > 0 ? <RsiFlags rsiFlags={dashboard.rsi_flags} /> : null}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {dashboard.risk_reward_summary ? <AvgWinLoss riskRewardSummary={dashboard.risk_reward_summary} /> : null}
          {dashboard.duration_histogram && dashboard.duration_histogram.length > 0 ? <DurationHistogram durationHist={dashboard.duration_histogram} /> : null}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {dashboard.streaks ? <Streaks streaks={dashboard.streaks} /> : null}
          {dashboard.rsi_scatter && dashboard.rsi_scatter.length > 0 ? <RsiScatterChart rsiScatter={dashboard.rsi_scatter} /> : null}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {dashboard.risk_reward && dashboard.risk_reward.length > 0 ? <RiskRewardChart riskReward={dashboard.risk_reward} /> : null}
          {dashboard.resolution_times && dashboard.resolution_times.length > 0 || dashboard.resolution_summary
            ? <ResolutionTimeChart resolutionTimes={dashboard.resolution_times} resolutionSummary={dashboard.resolution_summary} /> : null}
        </div>

        <div className="grid grid-cols-1 gap-4">
          {dashboard.markets && dashboard.markets.length > 0 ? <MarketsChart markets={dashboard.markets} /> : null}
        </div>

        <div className="grid grid-cols-1 gap-4">
          {dashboard.monthly_depth && dashboard.monthly_depth.length > 0 ? <MonthlyQualityTable monthlyDepth={dashboard.monthly_depth} /> : null}
        </div>

        <div className="grid grid-cols-1 gap-4">
          {dashboard.ticker_monthly && dashboard.ticker_monthly.length > 0 && dashboard.heatmap_months && dashboard.heatmap_months.length > 0
            ? <TickerHeatMap tickerMonthly={dashboard.ticker_monthly} heatmapMonths={dashboard.heatmap_months} /> : null}
        </div>
      </div>

      <TradesTable trades={trades} />
    </div>
  );
}
